import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { accessApi } from "../../services/accessApi";

const EMPTY_FORM = {
  user_id: "",
  permission_level: "agent",
};

export default function ITMailAccountAccessPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const [userQuery, setUserQuery] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
  const t = setTimeout(async () => {
    const q = userQuery.trim();
    if (!q) {
      setUserSuggestions([]);
      return;
    }

    try {
      setSearchingUsers(true);
      const res = await accessApi.searchMailboxUsers(q);
      setUserSuggestions(res?.data?.data || []);
    } catch (err) {
      console.error("searchMailboxUsers error:", err);
    } finally {
      setSearchingUsers(false);
    }
  }, 250);

  return () => clearTimeout(t);
}, [userQuery]);


  useEffect(() => {
    load();
  }, [id]);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await accessApi.getMailAccountAccess(id);
      setAccount(res?.data?.data?.account || null);
      setRows(res?.data?.data?.users || []);
    } catch (err) {
      console.error("ITMailAccountAccessPage load error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to load mailbox access.");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) =>
      [row.display_name, row.email, row.permission_level]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [rows, q]);

  const addAccess = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      await accessApi.createMailAccountAccess(id, {
        user_id: Number(selectedUser?.id || 0),
        permission_level: form.permission_level,
      });
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      console.error("addAccess error:", err);
      alert(err?.response?.data?.message || err.message || "Failed to add mailbox access.");
    } finally {
      setSaving(false);
    }
  };

  const updateLevel = async (rowId, permission_level) => {
    try {
      await accessApi.updateMailAccountAccess(rowId, { permission_level });
      await load();
    } catch (err) {
      console.error("updateLevel error:", err);
      alert(err?.response?.data?.message || err.message || "Failed to update mailbox access.");
    }
  };

  const removeAccess = async (rowId) => {
    if (!window.confirm("Remove mailbox access for this user?")) return;

    try {
      await accessApi.deleteMailAccountAccess(rowId);
      await load();
    } catch (err) {
      console.error("removeAccess error:", err);
      alert(err?.response?.data?.message || err.message || "Failed to remove mailbox access.");
    }
  };

  if (loading) return <div className="p-6 text-black dark:text-white">Loading mailbox access...</div>;
  if (error) return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">Mailbox Access</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {account?.display_name || account?.email || "Mail account"}
          </p>
        </div>

        <button
          onClick={() => navigate("/it/mail/accounts")}
          className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
        >
          Back
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <form onSubmit={addAccess} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
  <input
    value={selectedUser ? `${selectedUser.display_name} — ${selectedUser.email}` : userQuery}
    onChange={(e) => {
      setSelectedUser(null);
      setUserQuery(e.target.value);
    }}
    placeholder="Search user by name or email..."
    className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white w-full"
  />

  {!selectedUser && (userQuery.trim() || searchingUsers || userSuggestions.length > 0) && (
    <div className="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-lg overflow-hidden">
      {searchingUsers && (
        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          Searching...
        </div>
      )}

      {!searchingUsers &&
        userSuggestions.map((u) => (
          <button
            key={u.id}
            type="button"
            onClick={() => {
              setSelectedUser(u);
              setUserQuery("");
              setUserSuggestions([]);
            }}
            className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-neutral-900"
          >
            <div className="text-sm font-medium text-black dark:text-white">
              {u.display_name || u.full_name || "-"}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {u.email}
              {u.position_title ? ` • ${u.position_title}` : ""}
            </div>
          </button>
        ))}

      {!searchingUsers && !userSuggestions.length && userQuery.trim() && (
        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          No users found.
        </div>
      )}
    </div>
  )}
</div>

          <select
            value={form.permission_level}
            onChange={(e) => setForm((prev) => ({ ...prev, permission_level: e.target.value }))}
            className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="owner">owner</option>
            <option value="manager">manager</option>
            <option value="agent">agent</option>
            <option value="read">read</option>
          </select>

          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-60"
          >
            {saving ? "Adding..." : "Add Access"}
          </button>
        </form>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search assigned users..."
          className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
        />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Permission Level</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60"
                >
                  <td className="p-3 text-black dark:text-white">{row.display_name || "-"}</td>
                  <td className="p-3 text-black dark:text-white">{row.email || "-"}</td>
                  <td className="p-3">
                    <select
                      value={row.permission_level}
                      onChange={(e) => updateLevel(row.id, e.target.value)}
                      className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                    >
                      <option value="owner">owner</option>
                      <option value="manager">manager</option>
                      <option value="agent">agent</option>
                      <option value="read">read</option>
                    </select>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => removeAccess(row.id)}
                      className="px-3 py-2 rounded-xl border border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    No mailbox users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}